import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./layout.css";
import logo from "../assets/logo.png";

const NAV = [
  { label: "Home", href: "/#top", type: "scroll" },  // ← changed
  { label: "About", href: "/about", type: "route" },
  { label: "Gallery", href: "/gallery", type: "route" },
  // { label: "Supporters", href: "/patreon", type: "route" },
  { label: "Contact", href: "/contact", type: "route" }
];

/* ---------- Scroll hook ---------- */
const useScrollY = () => {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
};

// Styles are imported via layout.css, no dynamic injection needed.

/* ---------- Cursor ---------- */
const Cursor = () => {
  const dot = useRef(null);
  const ring = useRef(null);
  const m = useRef({ x: 0, y: 0 });
  const f = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const move = (e) => {
      m.current = { x: e.clientX, y: e.clientY };
      const mag = e.target?.closest("[data-mag]");
      document.querySelectorAll("[data-mag]").forEach(el => (el.style.transform = ""));
      if (mag) {
        const r = mag.getBoundingClientRect();
        mag.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * .2}px,${(e.clientY - r.top - r.height / 2) * .2}px)`;
      }
    };
    const enter = () => { dot.current?.classList.add("lg"); ring.current?.classList.add("lg"); };
    const leave = () => { dot.current?.classList.remove("lg"); ring.current?.classList.remove("lg"); };
    const attach = () => document.querySelectorAll("a,button,[data-mag]").forEach(el => {
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mouseleave", leave);
    });
    window.addEventListener("mousemove", move);
    attach();
    const obs = new MutationObserver(attach);
    obs.observe(document.body, { childList: true, subtree: true });

    const loop = () => {
      f.current.x += (m.current.x - f.current.x) * .13;
      f.current.y += (m.current.y - f.current.y) * .13;
      if (dot.current) { dot.current.style.left = m.current.x + "px"; dot.current.style.top = m.current.y + "px"; }
      if (ring.current) { ring.current.style.left = f.current.x + "px"; ring.current.style.top = f.current.y + "px"; }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      cancelAnimationFrame(raf.current);
      obs.disconnect();
    };
  }, []);

  return (
    <>
      <div ref={dot} className="c-dot" style={{ position: "fixed", pointerEvents: "none" }} />
      <div ref={ring} className="c-ring" style={{ position: "fixed", pointerEvents: "none" }} />
    </>
  );
};

/* ---------- NavLink component ---------- */
/* Handles both route links (/gallery) and scroll links (/#about) */
const NavLink = ({ item, onClick, className }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive =
    item.type === "route"
      ? location.pathname === item.href
      : false;

  const handleClick = (e) => {
    if (onClick) onClick();

    if (item.type === "scroll") {
      e.preventDefault();
      const hash = item.href.split("#")[1]; // e.g. "about", "top"

      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          if (hash === "top") window.scrollTo({ top: 0, behavior: "smooth" });
          else document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      } else {
        if (hash === "top") window.scrollTo({ top: 0, behavior: "smooth" });
        else document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  if (item.type === "route") {
    return (
      <Link
        to={item.href}
        className={`${className || ""} ${isActive ? "active" : ""}`}
        onClick={onClick}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <a
      href={item.href}
      className={`${className || ""} ${isActive ? "active" : ""}`}
      onClick={handleClick}
    >
      {item.label}
    </a>
  );
};

/* ---------- Header ---------- */
const Header = () => {
  const y = useScrollY();
  const [open, setOpen] = useState(false);
  const stuck = y > 60;
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(!!sessionStorage.getItem("token"));

  useEffect(() => {
    setIsLoggedIn(!!sessionStorage.getItem("token"));
  }, [location]);

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("accountId");
    sessionStorage.removeItem("mobileNumber");
    sessionStorage.removeItem("registered_player");
    sessionStorage.removeItem("selectedPlayerId");
    setIsLoggedIn(false);
    navigate("/");
  };

  return (
    <>
      <header className={`hdr ${stuck ? "stuck" : ""}`}>
        {/* Logo */}
        <Link
          to="/"
          className="hdr-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <div className="hdr-logo-circle">
            <img src={logo} alt="ADBA Logo" className="logo-img" />
          </div>
          <div className="hdr-logo-text">
            <span className="name">ADBA</span>
            <span className="tagline">Anand District Badminton Association</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hdr-nav">
          <ul>
            {NAV.map(n => (
              <li key={n.href}>
                <NavLink item={n} />
              </li>
            ))}
            {isLoggedIn && (
              <li>
                <NavLink item={{ label: "Dashboard", href: "/userdashboard", type: "route" }} />
              </li>
            )}
            <li>
              {isLoggedIn ? (
                <button onClick={handleLogout} className="btn-join" style={{ cursor: "pointer" }}>
                  Logout
                </button>
              ) : (
                <Link to="/login" className="btn-join">
                  Login
                </Link>
              )}
            </li>
          </ul>
        </nav>

        {/* Burger */}
        <button className="hdr-burger" onClick={() => setOpen(v => !v)} aria-label="Menu">
          <span style={open ? { transform: "rotate(45deg) translate(5px,5px)" } : {}} />
          <span style={open ? { opacity: 0 } : {}} />
          <span style={open ? { transform: "rotate(-45deg) translate(4px,-4px)" } : {}} />
        </button>
      </header>

      {/* Mobile menu */}
      <div className={`hdr-mobile ${open ? "open" : ""}`}>
        <button className="hdr-mobile-x" onClick={() => setOpen(false)}>✕</button>
        {NAV.map(n => (
          <NavLink
            key={n.href}
            item={n}
            onClick={() => setOpen(false)}
          />
        ))}
        {isLoggedIn && (
          <NavLink
            item={{ label: "Dashboard", href: "/userdashboard", type: "route" }}
            onClick={() => setOpen(false)}
          />
        )}
        <hr />
        {isLoggedIn ? (
          <button
            onClick={() => {
              handleLogout();
              setOpen(false);
            }}
            className="join"
            style={{
              fontFamily: "var(--fd)",
              fontSize: "48px",
              letterSpacing: "2px",
              color: "var(--gold)",
              background: "none",
              border: "none",
              cursor: "pointer"
            }}
          >
            LOGOUT
          </button>
        ) : (
          <Link
            to="/login"
            className="join"
            onClick={() => setOpen(false)}
            style={{
              fontFamily: "var(--fd)",
              fontSize: "48px",
              letterSpacing: "2px",
              color: "var(--gold)"
            }}
          >
            LOGIN
          </Link>
        )}
      </div>
    </>
  );
};

/* ---------- Footer ---------- */
const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleScroll = (hash) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="ftr">
      <div className="ftr-top">
        {/* Brand */}
        <div>
          <div className="ftr-brand-logo">
            <div className="ftr-brand-icon">
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                <circle cx="13" cy="13" r="11" stroke="rgba(255,255,255,.4)" strokeWidth="1.4" />
                <circle cx="13" cy="13" r="6" stroke="rgba(255,255,255,.75)" strokeWidth="1.4" />
                <line x1="13" y1="2" x2="13" y2="24" stroke="rgba(255,255,255,.35)" strokeWidth="1" />
                <line x1="2" y1="13" x2="24" y2="13" stroke="rgba(255,255,255,.35)" strokeWidth="1" />
              </svg>
            </div>
            <div>
              <span className="ftr-brand-name">ADBA</span>
              <span className="ftr-brand-sub">Anand District Badminton Association</span>
            </div>
          </div>
          <p className="ftr-brand-desc">
            Anand District Badminton Association is committed to fostering talent and
            promoting the sport within our community. Join us in our mission to cultivate
            excellence, sportsmanship, and a love for badminton.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <span className="ftr-col-head">Quick Links</span>
          <div className="ftr-links">
            <Link to="/">Home</Link>
            {/* <a href="#about" onClick={() => handleScroll("about")}>About</a> */}
            <Link to="/about">About</Link>
            <Link to="/gallery">Gallery</Link>
            <a href="#events" onClick={() => handleScroll("events")}>Events</a>
            <Link to="/contact">Contact</Link>
            {/* <a href="#events" onClick={() => handleScroll("events")}>Events</a>
            <a href="#contact" onClick={() => handleScroll("contact")}>Contact</a> */}
          </div>
        </div>

        {/* Affiliated to */}
        <div>
          <span className="ftr-col-head">Affiliated to</span>
          <div className="ftr-affils">
            {[
              { code: "./Gujarat-Badminton.png", name: "Gujarat Badminton Association", tag: "State Body" },
              { code: "./BAI-Logo.png", name: "Badminton Association of India", tag: "National Body" },
            ].map(a => (
              <div key={a.code} className="ftr-affil">
                <div ><img src={a.code} className="ftr-affil-badge" /> </div>
                <div>
                  <div className="ftr-affil-name">{a.name}</div>
                  <div className="ftr-affil-tag">{a.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ftr-bottom">
        <p className="ftr-copy">All Copyright © Reserved by <strong>ADBA</strong> · {new Date().getFullYear()}</p>
        <p className="ftr-copy">Anand District, Gujarat, India</p>
      </div>
    </footer>
  );
};

/* ---------- Layout ---------- */
const Layout = ({ children }) => {
  return (
    <>
      <div className="lay-noise" />
      <Cursor />
      <Header />
      <main className="lay-main">{children}</main>
      <Footer />
    </>
  );
};

export default Layout;
export { Header, Footer };

